import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

import { api, ApiError } from './api.js';

export interface WhoamiResponse {
  adminEmail: string;
  expiresAt: number;
  userId?: string | null;
  workspaceId?: string | null;
}

interface AuthContextValue {
  admin: WhoamiResponse | null;
  isLoading: boolean;
  isCheckingIdentity: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [checkingIdentity, setCheckingIdentity] = useState(false);
  const query = useQuery<WhoamiResponse | null>({
    queryKey: ['whoami'],
    queryFn: async () => {
      try {
        return await api.get<WhoamiResponse>('/api/v1/auth/whoami');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  const { refetch } = query;
  useEffect(() => {
    let pending = 0;
    let active = true;
    // Cookie 可在另一标签登录/切换工作区时变化，恢复可见前先重新确认身份。
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      pending += 1;
      setCheckingIdentity(true);
      void refetch().finally(() => {
        pending -= 1;
        if (active && pending === 0) setCheckingIdentity(false);
      });
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      active = false;
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [refetch]);

  const value: AuthContextValue = {
    admin: query.isError ? null : (query.data ?? null),
    isLoading: query.isLoading,
    isCheckingIdentity: checkingIdentity,
    login: async (email, password) => {
      // 密码请求不经过 MutationCache，离开登录页后没有持久的 variables.password。
      await api.post('/api/v1/auth/login', { email, password });
      await refetch({ throwOnError: true });
    },
    logout: async () => {
      await api.post('/api/v1/auth/logout');
      await qc.cancelQueries();
      qc.getMutationCache().clear();
      qc.setQueryData(['whoami'], null);
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth 必须在 AuthProvider 内使用');
  return ctx;
}
